import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Icons, Popover } from '@usepepper/sprout-kit-ui';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { Platform, View } from 'react-native';
import { TextInput } from '@/experiences/sales-portal/components/text-input';
import { Typography } from '@usepepper/ui/atoms/typography';

export const BOOKMARK_NOTE_MAX_LENGTH = 50;

type PopoverPlacement = React.ComponentProps<typeof Popover>['placement'];

interface Props {
  anchor: React.ReactNode;
  initialNote?: string | null;
  onSave: (note: string) => void;
  placement?: PopoverPlacement;
}

export const BookmarkNotePopover: React.FC<Props> = ({
  anchor,
  initialNote,
  onSave,
  placement = Platform.OS === 'web' ? 'bottom-start' : 'top-start',
}) => {
  const [styles, theme] = useStyles();
  const [note, setNote] = useState(initialNote ?? '');

  // The upsert fires once, when this popover goes away. A normal close is not
  // the only way that happens — a route change or a virtualized row unmounting
  // drops us with no open-change event, which would otherwise leave the store
  // (row icon, sidenav entry) claiming a bookmark that was never written. Refs
  // keep the cleanup mount-only; hasSaved stops the two paths double-writing.
  const hasSavedRef = useRef(false);
  const latestRef = useRef({ note, onSave });
  latestRef.current = { note, onSave };

  const save = useCallback(() => {
    if (hasSavedRef.current) {
      return;
    }
    hasSavedRef.current = true;
    latestRef.current.onSave(latestRef.current.note.trim());
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        save();
      }
    },
    [save],
  );

  useEffect(() => save, [save]);

  return (
    <Popover
      allowFlip
      offset={4}
      onOpenChange={handleOpenChange}
      open
      placement={placement}
    >
      <Popover.Anchor>{anchor}</Popover.Anchor>
      <Popover.Content disableFocusScope>
        <View style={styles.popover}>
          <View style={styles.header}>
            <View style={styles.title}>
              <Icons.Bookmark color={theme.sk.color.icon.secondary} size={14} />
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-sm-600"
              >
                {i18n.t('pricing.bookmarks.note.title')}
              </Typography>
            </View>
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm"
            >
              {`${note.length}/${BOOKMARK_NOTE_MAX_LENGTH}`}
            </Typography>
          </View>
          <TextInput
            autoFocus
            maxLength={BOOKMARK_NOTE_MAX_LENGTH}
            multiline
            onChangeText={setNote}
            onKeyPress={({ nativeEvent }) =>
              nativeEvent.key === Key.Escape && handleOpenChange(false)
            }
            placeholder={i18n.t('pricing.bookmarks.note.placeholder', {
              count: BOOKMARK_NOTE_MAX_LENGTH,
            })}
            placeholderTextColor={theme.sk.color.text.tertiary}
            style={styles.input}
            testID="bookmark-note-input"
            value={note}
          />
        </View>
      </Popover.Content>
    </Popover>
  );
};

const useStyles = createStyles((theme) => ({
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.sk.spacing.spaceSmall,
  },
  input: {
    fontSize: theme.sk.typography.paragraph.md.fontSize,
  },
  popover: {
    padding: theme.sk.spacing.spaceMedium,
    width: 280,
  },
  title: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
